import StatusChip from 'components/@extended/StatusChip';
import { ExpandMoreRounded, InfoOutlined as InfoOutlinedIcon } from 'components/icons';
import { useEffect, useRef } from 'react';
import { ClassifierData } from 'types/classifiers';
import { LawFirmListItemDto } from 'types/law-firms';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';
import { GroupLink, LastReferralCell, LawFirmPartyCell, MattersCell, ReferralsCell } from './LawFirmCells';

const SHOWN_GROUPS = 3;

/**
 * A contact in two or more Legal Services groups, as one full-width row: the contact beside a card per group,
 * each card's cells as wide as the columns above them, so a group's name and figures sit under their headers.
 * The widths are the table's column variables, less what the cards' inset and classic's border take (the new
 * design's card is edged by its shadow): the new design's cells keep 8px a side where classic's keep 16px, so its
 * cards start 8px left of the group column.
 */
const LawFirmGroupsBlock = ({
    lawFirm,
    classifiers,
    expanded,
    onShowMore,
}: {
    lawFirm: LawFirmListItemDto;
    classifiers: Record<string, ClassifierData>;
    expanded: boolean;
    onShowMore: (partyId: number) => void;
}) => {
    const groups = lawFirm.partyGroups;
    const shown = expanded ? groups : groups.slice(0, SHOWN_GROUPS);
    const hasMore = shown.length < groups.length;
    const firstRevealed = useRef<HTMLAnchorElement>(null);
    // Set by the click alone, so a block that mounts already open (scrolled back into view) takes no focus.
    const focusOnReveal = useRef(false);
    useEffect(() => {
        if (expanded && focusOnReveal.current) {
            focusOnReveal.current = false;
            firstRevealed.current?.focus();
        }
    }, [expanded]);

    return (
        <div className="flex w-full items-start">
            <div className="new:w-[calc(var(--utw-w-party)-8px)] new:pt-[19px] new:pr-2 new:pb-3 new:pl-5 w-(--utw-w-party) shrink-0 px-4 pt-[23px] pb-4">
                <LawFirmPartyCell party={lawFirm.party} classifiers={classifiers}>
                    <StatusChip
                        type="info"
                        icon={<InfoOutlinedIcon className="size-4" />}
                        label={t('law-firms.in-groups', { count: String(groups.length) })}
                        className="new:text-info-darker w-fit"
                    />
                </LawFirmPartyCell>
            </div>
            {/* The new design's 8px bottom grows to 10px under the "Show more" link, as classic's. */}
            <div className={cn('new:pt-2 flex min-w-0 flex-1 flex-col gap-2 py-2.5 pr-2', !hasMore && 'new:pb-2')}>
                <ul aria-label={t('law-firms.legal-services-groups')} className="flex flex-col gap-2">
                    {shown.map((group, index) => (
                        <li
                            key={group.id}
                            className="bg-paper border-divider new:border-0 new:shadow-card new:outline-hidden new:py-2.5 flex items-center rounded-lg border py-3"
                        >
                            <div className="new:w-[calc(var(--utw-w-legalServicesGroup)+8px)] new:pr-2 new:pl-4 w-[calc(var(--utw-w-legalServicesGroup)-1px)] shrink-0 pr-4 pl-[15px]">
                                <GroupLink group={group} linkRef={index === SHOWN_GROUPS ? firstRevealed : undefined} />
                            </div>
                            <div className="new:px-2 w-(--utw-w-relatedMatters) shrink-0 px-4">
                                <MattersCell figures={group} label={t('law-firms.group-cases')} />
                            </div>
                            <div className="new:px-2 w-(--utw-w-referrals) shrink-0 px-4">
                                <ReferralsCell figures={group} />
                            </div>
                            <div className="new:pl-2 new:w-[calc(var(--utw-w-lastGroupReferral)-8px)] new:pr-4 w-[calc(var(--utw-w-lastGroupReferral)-9px)] shrink-0 pr-[15px] pl-4">
                                <LastReferralCell figures={group} />
                            </div>
                        </li>
                    ))}
                </ul>
                {hasMore && (
                    // It reveals and does not toggle, so no aria-expanded; the row ignores a click on a button.
                    <div className="pl-4">
                        <button
                            type="button"
                            className="text-link text-body2 inline-flex w-fit cursor-pointer items-center gap-0.5 font-medium hover:underline"
                            onClick={() => {
                                focusOnReveal.current = true;
                                onShowMore(lawFirm.party.id);
                            }}
                        >
                            {t('law-firms.show-more-groups', { count: String(groups.length - shown.length) })}
                            <ExpandMoreRounded className="size-4" />
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default LawFirmGroupsBlock;
